import { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShoppingCart,
  Loader2,
  Plus,
  Minus,
  Trash2,
  Package,
  Tag,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import { lookupCoupon, lookupPricing, fetchShippingQuote, applyUpgradeCode, syncCart } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import CheckoutFlow from "./CheckoutFlow";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const CART_KEY = (slug) => `postcue.cart.${slug}`;
const COUPON_KEY = (slug) => `postcue.coupon.${slug}`;

function readCart(slug) {
  try {
    const raw = localStorage.getItem(CART_KEY(slug));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function readCoupon(slug) {
  try {
    const raw = localStorage.getItem(COUPON_KEY(slug));
    if (!raw) return { code: "", info: null };
    const c = JSON.parse(raw);
    return { code: c.code || "", info: c.info || null };
  } catch {
    return { code: "", info: null };
  }
}

/**
 * CartSheet — mindfulminerals-style cart panel.
 * Outer just owns Sheet open/close.  The inner body mounts ONLY when open
 * so it can hydrate from localStorage via useState initializers (avoids the
 * react-hooks/set-state-in-effect lint rule).
 */
export default function CartSheet({
  open,
  onOpenChange,
  dealerSlug,
  products,
  user,
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto p-0 bg-white"
        data-testid="cart-sheet"
      >
        {open && (
          <CartBody
            dealerSlug={dealerSlug}
            products={products}
            user={user}
            onClose={() => onOpenChange(false)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function CartBody({ dealerSlug, products, user, onClose }) {
  const { adoptUser } = useAuth();
  const [cart, setCart] = useState(() => readCart(dealerSlug));
  const initialCoupon = useMemo(() => readCoupon(dealerSlug), [dealerSlug]);
  const [coupon, setCoupon] = useState(initialCoupon.code);
  const [couponInfo, setCouponInfo] = useState(initialCoupon.info);
  const [couponErr, setCouponErr] = useState("");
  const [applying, setApplying] = useState(false);
  const [pricing, setPricing] = useState({}); // { id: {msrp, role_price, weight} }
  const [refreshing, setRefreshing] = useState(false);
  // Multi-step checkout state — when `inCheckout` is true the cart list
  // hides and the <CheckoutFlow> takes over the sheet body.
  const [inCheckout, setInCheckout] = useState(false);

  // The effective tier for line pricing: coupon overrides signed-in role.
  const tier = useMemo(() => {
    const t =
      couponInfo?.coupon_tier ||
      user?.role_name ||
      user?.role ||
      "";
    return (t || "").toString().trim().toLowerCase();
  }, [couponInfo, user]);

  const taxExempt = Number(user?.tax_exempted || 0) === 1;

  // Persist cart on change
  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY(dealerSlug), JSON.stringify(cart));
    } catch {
      /* ignore */
    }
  }, [cart, dealerSlug]);

  // Refresh pricing whenever cart contents or tier changes.
  /* eslint-disable */
  useEffect(() => {
    const pids = Object.keys(cart).filter((id) => cart[id] > 0);
    if (pids.length === 0) {
      setPricing({});
      return undefined;
    }
    let active = true;
    setRefreshing(true);
    (async () => {
      try {
        const res = await lookupPricing({ productRecordIds: pids, tier });
        if (!active) return;
        const map = {};
        for (const it of res.items || []) {
          map[it.product_record_id] = it;
        }
        setPricing(map);
      } catch {
        if (active) setPricing({});
      } finally {
        if (active) setRefreshing(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [cart, tier]);
  /* eslint-enable */

  const productById = useMemo(() => {
    const m = {};
    for (const p of products || []) m[p.id] = p;
    return m;
  }, [products]);

  // BOGO cart logic: when a product has an active BOGO promo, we
  // auto-fill free units alongside paid units.  The cart record stores
  // ONLY the paid qty (what the user clicked Add for); free units are
  // computed on render.  This way, decreasing the paid qty automatically
  // shrinks (or removes) the free units.
  function bogoFreeFor(p, paidQty) {
    const promo = p?.promo;
    if (!promo || !promo.active) return 0;
    const buy = Number(promo.buy_qty || 0);
    const free = Number(promo.free_qty || 0);
    if (buy <= 0 || free <= 0) return 0;
    return Math.floor(Number(paidQty || 0) / buy) * free;
  }

  const lines = useMemo(() => {
    const out = [];
    for (const [id, paidQty] of Object.entries(cart)) {
      if (!paidQty || paidQty <= 0) continue;
      const p = productById[id];
      if (!p) continue;
      const pi = pricing[id] || {};
      const msrp = Number(pi.msrp ?? p.price ?? 0);
      const rolePrice = Number(pi.role_price ?? p.price ?? 0);
      // Sale price (date-aware `on_sale` from /postcue/pricing) wins over the
      // role price — mirrors what the product page charges.
      const onSale = !!pi.on_sale && Number(pi.sale_price || 0) > 0;
      const unitPrice = onSale ? Number(pi.sale_price) : rolePrice;
      const freeQty = bogoFreeFor(p, paidQty);
      // Paid line
      out.push({
        id,
        key: `${id}-paid`,
        kind: "paid",
        name: p.name,
        image_url: p.image_url,
        qty: paidQty,
        promo: p.promo || null,
        msrp,
        on_sale: onSale,
        role_price: unitPrice,
        line_msrp: msrp * paidQty,
        line_total: unitPrice * paidQty,
        weight: Number(pi.weight || 0),
      });
      // Free duplicate line (BOGO) — shows as its own ShipStation row.
      if (freeQty > 0) {
        out.push({
          id,
          key: `${id}-free`,
          kind: "free",
          name: p.name,
          image_url: p.image_url,
          qty: freeQty,
          promo: p.promo || null,
          msrp,
          role_price: 0,
          line_msrp: 0,
          line_total: 0,
          weight: Number(pi.weight || 0),
        });
      }
    }
    return out;
  }, [cart, productById, pricing]);

  const subMSRP = lines.reduce((s, l) => s + l.line_msrp, 0);
  const subRole = lines.reduce((s, l) => s + l.line_total, 0);
  const discount = Math.max(0, subMSRP - subRole);
  // Both paid and free rows ship — sum weights across all rows.
  const totalWeight = lines.reduce((s, l) => s + l.weight * l.qty, 0);

  // Shipping: pulled live from Airtable `Shipping` table via the backend.
  // Formula (matches iOS app + chatbot): (Per-oz × total_weight) + Flat fee
  // for the row whose `user_role` matches the buyer's `tier` and whose
  // `seller_id` = 1 (OreVital admin — only seller fulfilling on this portal).
  const [shippingQuote, setShippingQuote] = useState(null);
  useEffect(() => {
    if (lines.length === 0) {
      setShippingQuote(null);
      return;
    }
    let cancelled = false;
    fetchShippingQuote({
      sellerId: 1,
      userRole: tier || "customer",
      totalWeight,
    })
      .then((q) => {
        if (!cancelled) setShippingQuote(q);
      })
      .catch(() => {
        if (!cancelled) setShippingQuote(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tier, totalWeight, lines.length]);

  const shipping = shippingQuote?.total ?? 0;
  const tax = taxExempt ? 0 : Math.round(subRole * 0.07 * 100) / 100;
  const total = Math.round((subRole + shipping + tax) * 100) / 100;

  const clearCart = () => {
    setCart({});
    try {
      localStorage.removeItem(CART_KEY(dealerSlug));
      localStorage.removeItem(COUPON_KEY(dealerSlug));
    } catch {
      /* ignore */
    }
    const recId = user?.user_record_id || user?.record_id;
    if (recId) syncCart(recId, [], "").catch(() => {});
    toast.success("Cart cleared");
  };
  const inc = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) =>
    setCart((c) => {
      const next = { ...c };
      if (!next[id]) return c;
      next[id] -= 1;
      if (next[id] <= 0) delete next[id];
      return next;
    });
  const remove = (id) =>
    setCart((c) => {
      const next = { ...c };
      delete next[id];
      return next;
    });

  async function applyCoupon() {
    const code = coupon.trim();
    if (!code) {
      setCouponInfo(null);
      setCouponErr("");
      try {
        localStorage.removeItem(COUPON_KEY(dealerSlug));
      } catch {
        /* ignore */
      }
      return;
    }
    setApplying(true);
    setCouponErr("");
    try {
      const res = await lookupCoupon(code);
      if (res?.valid) {
        const info = {
          coupon_name: res.coupon_name,
          coupon_tier: res.coupon_tier,
          ambassador_name: res.ambassador_name,
        };
        setCouponInfo(info);
        try {
          localStorage.setItem(
            COUPON_KEY(dealerSlug),
            JSON.stringify({ code, info })
          );
        } catch {
          /* ignore */
        }
        toast.success(`Coupon applied: ${info.coupon_tier || "discount"} tier`);
        // If the shopper is signed in, apply the tier as a permanent account
        // upgrade (upward only; admin/dist are never granted server-side).
        const uRec = user?.record_id || user?.user_record_id;
        if (uRec) {
          try {
            const up = await applyUpgradeCode(uRec, code);
            if (up?.upgraded) {
              adoptUser({ ...user, role_name: up.new_role, role: up.new_role });
              toast.success(
                `🎉 Your account has been upgraded to ${up.new_role} pricing!`
              );
            }
          } catch {
            /* non-blocking: pricing still applies for this session */
          }
        }
      } else {
        setCouponInfo(null);
        setCouponErr(res?.message || "Coupon not found");
        try {
          localStorage.removeItem(COUPON_KEY(dealerSlug));
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      setCouponErr(e?.response?.data?.detail || "Could not validate coupon");
    } finally {
      setApplying(false);
    }
  }

  async function placeOrder() {
    if (lines.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    if (!user) {
      toast.error("Please sign in to continue");
      return;
    }
    setInCheckout(true);
  }

  return (
    <>
      <SheetHeader className="px-5 pt-5 pb-3 border-b border-zinc-200">
        <SheetTitle className="flex items-center gap-2 text-lg">
          <ShoppingCart size={18} className="text-emerald-600" />
          {inCheckout ? "Checkout" : "Your cart"}
        </SheetTitle>
        <SheetDescription className="text-xs text-zinc-500">
          {inCheckout
            ? "Shipping → Payment → Done"
            : couponInfo
            ? `${couponInfo.coupon_tier} pricing applied via ${couponInfo.coupon_name}`
            : tier
              ? `${tier} pricing applied (your role)`
              : "Add a coupon code to unlock dealer pricing."}
        </SheetDescription>
      </SheetHeader>

      {inCheckout ? (
        <CheckoutFlow
          user={user}
          dealerSlug={dealerSlug}
          lines={lines}
          subRole={subRole}
          shipping={shipping}
          tax={tax}
          total={total}
          couponCode={couponInfo?.coupon_name || ""}
          onClose={() => {
            setInCheckout(false);
            onClose();
          }}
          onSuccess={() => {
            // Order saved — clear the cart so reopening starts fresh.
            setCart({});
            try {
              localStorage.removeItem(CART_KEY(dealerSlug));
            } catch {
              /* ignore */
            }
          }}
        />
      ) : (
      <div className="px-5 py-4 space-y-4">
        {lines.length === 0 ? (
          <div className="py-10 text-center text-zinc-500 text-sm">
            <Package className="mx-auto text-zinc-400 mb-1" size={24} />
            Your cart is empty.
          </div>
        ) : (
          <>
          <div className="flex justify-end -mb-2">
            <button type="button" onClick={clearCart} className="text-xs font-semibold text-rose-600 hover:text-rose-700 inline-flex items-center gap-1" data-testid="cart-clear-btn">
              <Trash2 size={12} /> Clear cart
            </button>
          </div>
          <ul className="divide-y divide-zinc-200 rounded-2xl border border-emerald-200">
            {lines.map((l) => (
              <li
                key={l.key}
                className={`p-3 flex items-start gap-3 ${
                  l.kind === "free" ? "bg-rose-50/60" : ""
                }`}
                data-testid={`cart-line-${l.key}`}
              >
                {l.image_url ? (
                  <img
                    src={l.image_url}
                    alt={l.name}
                    className="w-14 h-14 rounded-lg object-cover bg-zinc-100"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400">
                    <Package size={18} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate flex items-center gap-1.5">
                    {l.name}
                    {l.kind === "free" && (
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-600 text-white"
                        data-testid={`cart-free-badge-${l.id}`}
                      >
                        🎁 FREE
                      </span>
                    )}
                  </div>
                  <div className="text-xs mt-0.5 flex items-baseline gap-2">
                    {l.kind === "free" ? (
                      <>
                        <span className="line-through text-zinc-400">
                          {fmt(l.msrp)}
                        </span>
                        <span className="font-bold text-rose-700">
                          FREE ({l.promo?.label})
                        </span>
                      </>
                    ) : (
                      <>
                        {l.msrp > l.role_price && (
                          <span className="line-through text-zinc-400">
                            {fmt(l.msrp)}
                          </span>
                        )}
                        <span className="font-bold text-emerald-700">
                          {fmt(l.role_price)}
                        </span>
                      </>
                    )}
                  </div>
                  {l.kind === "paid" ? (
                    <div className="mt-2 inline-flex items-center rounded-lg border border-zinc-200">
                      <button
                        onClick={() => dec(l.id)}
                        className="w-7 h-7 rounded-l-lg hover:bg-zinc-50 text-zinc-700"
                        data-testid={`cart-dec-${l.id}`}
                      >
                        <Minus size={12} className="mx-auto" />
                      </button>
                      <span
                        className="w-7 text-center text-sm font-semibold"
                        data-testid={`cart-qty-${l.id}`}
                      >
                        {l.qty}
                      </span>
                      <button
                        onClick={() => inc(l.id)}
                        className="w-7 h-7 rounded-r-lg hover:bg-zinc-50 text-zinc-700"
                        data-testid={`cart-inc-${l.id}`}
                      >
                        <Plus size={12} className="mx-auto" />
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] text-rose-700 font-semibold">
                      Qty {l.qty} · auto-added with{" "}
                      {l.promo?.buy_qty} paid
                    </div>
                  )}
                </div>
                <div className="text-right text-sm font-semibold text-zinc-900 shrink-0">
                  {l.kind === "free" ? (
                    <span className="text-rose-700">{fmt(0)}</span>
                  ) : (
                    fmt(l.line_total)
                  )}
                  {l.kind === "paid" && (
                    <button
                      onClick={() => remove(l.id)}
                      className="mt-1 block ml-auto text-rose-500 hover:text-rose-600"
                      data-testid={`cart-rm-${l.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          </>
        )}

        {lines.length > 0 && (
          <div className="rounded-2xl border border-zinc-200 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1">
              <Tag size={11} />
              Promo Code (optional)
            </div>
            <div className="flex gap-2">
              <Input
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="Enter code"
                className="h-9 rounded-xl text-sm"
                data-testid="cart-coupon-input"
              />
              <Button
                onClick={applyCoupon}
                disabled={applying}
                className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4"
                data-testid="cart-coupon-apply"
              >
                {applying ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  "Apply"
                )}
              </Button>
            </div>
            {couponErr && (
              <div className="text-xs text-rose-600">{couponErr}</div>
            )}
            {couponInfo && (
              <div className="text-xs text-emerald-700">
                ✓ {couponInfo.coupon_name} — {couponInfo.coupon_tier} (
                {couponInfo.ambassador_name})
              </div>
            )}
          </div>
        )}

        {lines.length > 0 && (
          <div
            className="rounded-2xl bg-zinc-50 p-4 space-y-1.5 text-sm"
            data-testid="cart-summary"
          >
            {discount > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>
                  Discount ({couponInfo?.coupon_tier || tier || ""})
                </span>
                <span data-testid="cart-discount">−{fmt(discount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-zinc-500">Tax</span>
              {taxExempt ? (
                <span
                  className="font-semibold tracking-wide text-zinc-500"
                  data-testid="cart-tax"
                >
                  EXEMPT
                </span>
              ) : (
                <span data-testid="cart-tax">{fmt(tax)}</span>
              )}
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">
                Shipping
                {shippingQuote?.title && (
                  <span
                    className="ml-1.5 text-[10px] text-zinc-400"
                    title={`${shippingQuote.title} — ($${shippingQuote.per_oz.toFixed(2)} × ${shippingQuote.weight.toFixed(2)} oz) + $${shippingQuote.flat_fee.toFixed(2)}`}
                  >
                    · {shippingQuote.title}
                  </span>
                )}
              </span>
              <span data-testid="cart-shipping">{fmt(shipping)}</span>
            </div>
            <div className="border-t border-zinc-200 my-2" />
            <div className="flex justify-between text-base">
              <span className="font-semibold text-zinc-900">Total</span>
              <span
                className="font-bold text-emerald-700"
                data-testid="cart-total"
              >
                {fmt(total)}
              </span>
            </div>
            {refreshing && (
              <div className="text-[10px] text-zinc-400 flex items-center gap-1 pt-1">
                <Loader2 size={10} className="animate-spin" /> refreshing pricing…
              </div>
            )}
          </div>
        )}

        {lines.length > 0 && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCart({ ...cart })}
              disabled={refreshing}
              className="rounded-xl"
              data-testid="cart-refresh"
            >
              <RefreshCw
                size={13}
                className={`mr-1 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <Button
              onClick={placeOrder}
              disabled={!user || lines.length === 0}
              className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid="cart-checkout"
            >
              {user ? (
                <>
                  Continue to Checkout
                  <ArrowRight size={13} className="ml-1" />
                </>
              ) : (
                "Sign in to checkout"
              )}
            </Button>
          </div>
        )}
      </div>
      )}
    </>
  );
}
